import * as _ from "lodash";
import * as React from "react";

import * as Models from "../Models";
import { selectLink, linkToRelativeUrl } from "./links";
import { getItemGroupColumnClass } from "./table";
import { OutotecIcons } from "../Components/General/Icons";
import { CHEVRON_DOWN, CHEVRON_RIGHT, REMOVE, INFO } from "../Constants";
import { Row } from "react-table";

export const getHeaderTitles: Function = (
    headers: string[],
    rows: Models.AttributeRow[]
) => {
    let headerTitles: any[] = headers.map((h: any) => {
        return {
            headerName: h,
            headerAccessor: h.replace(/ /g, "_").toLowerCase(),
        };
    });

    return headerTitles;
};

export const getData: Function = (
    headers: string[],
    rows: Models.GroupedAttributeRow[]
) => {
    return _.map(rows, (r) => {
        const items: Models.AttributeCell[] = r.items;
        const row: any = {};

        _.forEach(
            getHeaderTitles(headers, rows),
            (h: { headerName: string; headerAccessor: string }, i: number) => {
                row[h.headerAccessor] = items[i];
            }
        );

        if (r.children) {
            row.children = r.children;
        }

        return row;
    });
};

export const countDocumentRows: Function = (rows: Models.AttributeRow[]) => {
    return rows.filter(
        (r) => r.items && r.items.length >= 2 && !!r.items[1].url.url
    ).length;
};

export const createChildRows: Function = (
    originalRows: Models.AttributeRow[]
) => {
    var rows: Models.GroupedAttributeRow[] = [];

    var groups: _.Dictionary<Models.AttributeRow[]> = _.groupBy(
        originalRows,
        (r: Models.AttributeRow) => {
            // Disabled all grouping for now. Let's check this during IB-1548
            return "unknown";

            if (
                r.items.length < 6 ||
                !r.items[0].description ||
                !r.items[5].url.description
            ) {
                return "unknown";
            }

            // Document Name--Document ID--Document Revision--Functional Location ID
            if (r.items[1].additionalInfo) {
                return `${r.items[0].description}--${r.items[1].additionalInfo}--${r.items[5].url.description}`;
            }

            if (r.items[1].url.description) {
                return `${r.items[0].description}--${r.items[1].url.description}--${r.items[2].description}--${r.items[5].url.description}`;
            }

            return "unknown";
        }
    );

    Object.keys(groups).forEach((key: string) => {
        let group: Models.GroupedAttributeRow[] = groups[key];

        if (
            key !== "unknown" &&
            group.length > 1 &&
            group[0].items.length >= 2
        ) {
            let header: Models.GroupedAttributeRow = _.cloneDeep(group[0]);
            let members: Models.GroupedAttributeRow[];

            // Get Group type
            const isArchive: boolean =
                header.items[1].url.url &&
                header.items[1].url.url.trim().endsWith(".zip");
            if (isArchive) {
                members = group.slice(1);

                const paths: string[] = members
                    .map((m) => m.items[1].description)
                    .sort((a, b) => b.length - a.length);

                let result: any[] = [];
                let level: any = { result };

                paths.forEach((path) => {
                    path.split("/").reduce((r, name) => {
                        if (name != "" && !r[name]) {
                            r[name] = { result: [] };
                            r.result.push({ name, children: r[name].result });
                        }
                        return r[name];
                    }, level);
                });

                header.children = result;
            } else {
                // Duplicates, create header row to group
                members = _.cloneDeep(group);
                header.items[1].description = header.items[1].url.description;
                header.items[1].url = {} as Models.Link;
                members.forEach((c) => {
                    if (c.items[1].url.url) {
                        let fileName: string = c.items[1].url.url.substring(
                            c.items[1].url.url.lastIndexOf("/") + 1
                        );
                        c.items[1].url.description = fileName;
                        c.items[1].toolTip = fileName;
                    }
                });
                header.children = [...members];
            }

            rows.push(header);
        } else {
            rows.push(...group);
        }
    });

    return rows;
};

const customFilterMethod = (rows, columnId, filterValue) => {
    return rows.filter((row) => {
        const col = row.values[columnId]; // Get the value of the column
        let colDescription = "";

        if (col?.description && Array.isArray(col.description)) {
            colDescription =
                col.description.length > 0 ? col.description[0] : "";
        } else {
            colDescription = col?.description || col?.url?.description || "";
        }

        // If there's no description or it's null, skip this row
        if (colDescription === null || colDescription === "") {
            return false;
        }

        // Return rows that match the filter value (case-insensitive)
        return colDescription.toLowerCase().includes(filterValue.toLowerCase());
    });
};

const sortByDescription = (rowA, rowB, columnId, desc) => {
    const a = rowA.values[columnId];
    const b = rowB.values[columnId];

    // Get description from the row data
    const descriptionA = a?.description
        ? a.description
        : a?.url?.description || "";
    const descriptionB = b?.description
        ? b.description
        : b?.url?.description || "";

    // If either is null, return 0
    if (descriptionA === null || descriptionB === null) {
        return 0;
    }

    // If both descriptions are numbers, sort numerically
    if (_.isNumber(descriptionA) && _.isNumber(descriptionB)) {
        return descriptionA - descriptionB;
    }

    // Otherwise, sort alphabetically using localeCompare
    return descriptionA.toString().localeCompare(descriptionB.toString());
};

export const getItemGroupData: Function = (
    headers: string[],
    rows: Models.AttributeRow[]
): Models.DataGridContent => {
    const result: Models.DataGridContent = {
        columns: [],
        data: [],
    };

    const headerTitles: any[] = getHeaderTitles(headers, rows);

    result.columns = headerTitles.map(
        (h: { headerName: string; headerAccessor: string }) => {
            return {
                Header: h.headerName,
                accessor: h.headerAccessor,
                Filter: ({ column }) => (
                    <input
                        type="text"
                        value={column.filterValue || ""}
                        onChange={(e) => column.setFilter(e.target.value)}
                        placeholder="Filter..."
                        className="form-control"
                        style={{ width: "100%" }}
                    />
                ),
                filter: customFilterMethod,
                sortType: sortByDescription,
                Cell: (col: any) => {
                    const attributeCell: Models.AttributeCell = col.value;

                    return !attributeCell ? (
                        ""
                    ) : (
                        <span
                            title={
                                attributeCell.toolTip ||
                                attributeCell.description
                            }
                        >
                            {attributeCell.url && attributeCell.url.id && (
                                <a
                                    href={linkToRelativeUrl(attributeCell.url)}
                                    onClick={(e) => {
                                        selectLink(e, attributeCell.url);
                                    }}
                                >
                                    {attributeCell.url.description}
                                </a>
                            )}
                            {attributeCell.description}
                        </span>
                    );
                },
                getProps: (state, rowInfo, column) => {
                    const className: string = getItemGroupColumnClass(
                        rows,
                        rowInfo,
                        column
                    );
                    return { className };
                },
            };
        }
    );

    const docName: number = result.columns.findIndex(
        (c) => c.Header === "Document Name"
    );
    if (docName > -1) {
        result.columns[docName].style = { textTransform: "uppercase" };
    }

    const uploadedBy: number = result.columns.findIndex(
        (c) => c.Header === "Uploaded By"
    );
    if (uploadedBy > -1) {
        result.columns[uploadedBy].show = false;
    }

    result.data = getData(headers, rows);

    return result;
};

export const withExpander: (
    content: Models.DataGridContent
) => Models.DataGridContent = (content) => {
    const expander: any = {
        id: "expander",
        width: 20,
        maxWidth: 20,
        Header: "",
        Cell: ({ row }: { row: Row }) => {
            if (row.original.children && row.original.children.length > 0) {
                return (
                    <span {...row.getToggleRowExpandedProps()}>
                        {row.isExpanded && <OutotecIcons type={CHEVRON_DOWN} />}
                        {!row.isExpanded && (
                            <OutotecIcons type={CHEVRON_RIGHT} />
                        )}
                    </span>
                );
            }
            return <div />;
        },
        getProps: (state, rowInfo, column) => {
            if (
                !rowInfo.original.children ||
                !rowInfo.original.children.length
            ) {
                return { "data-childrowcount": 0 };
            }
            return { "data-childrowcount": rowInfo.original.children.length };
        },
        style: {
            paddingLeft: "5px",
        },
    };

    return { ...content, columns: [expander].concat(content.columns) };
};

export const withRemover: (
    content: Models.DataGridContent,
    actionFunction: (
        documentMeta: Models.DocumentMeta,
        fileName: string
    ) => void
) => Models.DataGridContent = (content, deleteFunction) => {
    const remover: any = {
        Header: "",
        id: "remover",
        width: 30,
        maxWidth: 30,
        Cell: ({ row }) => {
            const documentId: string =
                row.original.document_id &&
                row.original.document_id.url &&
                row.original.document_id.url.additionalInfo;
            const canDelete: boolean =
                documentId && documentId.indexOf("upload--") === 0;
            if (canDelete) {
                const meta: Models.DocumentMeta = {
                    documentId: documentId,
                    created: row.original.effective_date
                        ? row.original.effective_date.description
                        : "",
                    createdBy: row.original.uploaded_by
                        ? row.original.uploaded_by.description
                        : "",
                } as Models.DocumentMeta;

                const fileName: string = documentId.substring(
                    documentId.indexOf("--", documentId.indexOf("--") + 2) + 2
                );
                return (
                    <span
                        title={"Remove file"}
                        className="button-removefile"
                        onClick={(e) => deleteFunction(meta, fileName)}
                    >
                        <OutotecIcons type={REMOVE} />
                    </span>
                );
            } else {
                return <div />;
            }
        },
    };

    return { ...content, columns: content.columns.concat(remover) };
};
